import { Button, Radio, RadioGroup, Text } from "@ttc3k/trekker";
import { Group } from "iconoir-react";
import React, { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IInviteStaffMember, InviteStaffMember } from "shared/components";
import {
  InviteStaffMemberFormValues,
  useInviteStaffMemberForm,
} from "shared/components/organisms/InviteStaffModal/useInviteStaffMemberForm";
import { WhereaboutsModal } from "shared/components/root";
import { Box, Divider, Flex } from "styled-system/jsx";
import { useInviteStaff } from "./useInviteStaff";
import { useLoggedInAs } from "shared/hooks";
import { EnumOwnerType } from "gql/generated";
import { useAppContext } from "AppContext";

type InviteStaffModalProps = {
  entityId: string;
  isOpen: boolean;
  onClose: () => void;
  onInviteStaffClick: (
    sendInvitationEmail: boolean,
    staffToInvite: IInviteStaffMember[],
  ) => Promise<void>;
};

export const InviteStaffModal = ({
  entityId,
  isOpen,
  onClose,
  onInviteStaffClick,
}: InviteStaffModalProps) => {
  const { t } = useTranslation();

  // We need to determine the default selection for the send email radio group
  // To do so, we get all of the organizations the user has access to and check
  // to see if the user is logged in as that organization. If so, we use the
  // organization's metadata to determine the default selection.
  const { organizations } = useAppContext();
  const loggedInAs = useLoggedInAs();
  let sendInvitationEmail = true;
  if (loggedInAs && loggedInAs.type === EnumOwnerType.Organization) {
    const organization = organizations.find(
      (org) => org._id === loggedInAs.entityId,
    );
    if (organization) {
      // If there is no metadata, we default to sending an email
      sendInvitationEmail =
        organization.metadata?.inviteWhenAddingStaff ?? true;
    }
  }

  // Initialize the form and zod
  const [formError, setFormError] = useState<string | undefined>();
  const { form, onSubmit, defaultValues } = useInviteStaffMemberForm({
    sendInvitationEmail,
    dataProcessor: onInviteStaffClick,
    onSuccess: () => {
      form.reset(defaultValues);
      onClose();
    },
    onError: (message?: string) => {
      setFormError(message);
    },
  });

  // Ensure the form doesn't start off displaying an email validation error
  // when you start typing in that box (which happens AFTER sending an invitation
  // and then re-opening the modal)
  useEffect(() => {
    form.reset();
  }, [form, isOpen]);

  const {
    staffMembersToInvite,
    handleAddStaffMember,
    handleRemoveStaffMember,
    handleCloseClick,
  } = useInviteStaff({
    form,
    onClose,
  });

  const formId = "invite-staff-modal-form";

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={() =>
        handleCloseClick(form, defaultValues as InviteStaffMemberFormValues)
      }
      hideCloseTrigger={form.formState.isSubmitting}
      title={t("core:STAFF.INVITE_MODAL.TITLE")}
      HeaderIcon={Group}
      cancelButtonProps={{
        onClick: () =>
          handleCloseClick(form, defaultValues as InviteStaffMemberFormValues),
      }}
      saveButtonCopy={
        form.watch("sendInvitationEmail")
          ? t("core:STAFF.INVITE_MODAL.CTA_BUTTON_INVITE")
          : t("core:STAFF.INVITE_MODAL.CTA_BUTTON_ADD_STAFF")
      }
      saveButtonProps={{
        visual: "accent.blue",
        form: formId,
        type: "submit",
        isLoading: form.formState.isSubmitting,
        disabled: !form.formState.isDirty || form.formState.isSubmitting,
      }}
      disableBodyPadding={true}
    >
      <Box p="36px">
        <FormProvider {...form}>
          <form onSubmit={onSubmit} id={formId}>
            <Flex flexDir="column" alignItems="flex-start" gap="300">
              {staffMembersToInvite.map((staffMemberToInvite, i) => (
                <React.Fragment key={`${i}_${staffMemberToInvite.id}`}>
                  <InviteStaffMember
                    entityId={entityId}
                    form={form}
                    inputNameEmailProps={{ name: `toInvite.${i}.email` }}
                    inputNameFirstNameProps={{
                      name: `toInvite.${i}.firstName`,
                    }}
                    inputNameLastNameProps={{ name: `toInvite.${i}.lastName` }}
                    index={i}
                    onRemoveStaffToInvite={() => handleRemoveStaffMember(i)}
                  />
                  <Divider color="border.light" />
                </React.Fragment>
              ))}
              <Button
                visual="unstyled"
                onClick={handleAddStaffMember}
                type="button"
              >
                <Text color="text.accent.blue.mid">
                  + {t("core:STAFF.INVITE_MODAL.CTA_BUTTON_ADD")}
                </Text>
              </Button>

              {/* Determine if we need to send an email when the staff member(s) is added */}
              <Box pt="30px" pb="20px">
                <Text visual="bodySemiBold" mb="100">
                  {t("core:STAFF.INVITE_MODAL.LABELS.INVITATIONS")}
                </Text>
                <Text visual="bodyRegular" color="text.mid" mb="250">
                  {t("core:STAFF.INVITE_MODAL.LABELS.INVITATIONS_DESCRIPTION")}
                </Text>

                {/* Since the Trekker RadioGroup and Radio buttons can only have string values, we need to convert the boolean to a string */}
                <RadioGroup
                  value={form.watch("sendInvitationEmail").toString()}
                  onValueChange={({ value }: { value: string }) => {
                    form.setValue("sendInvitationEmail", value === "true");
                  }}
                  gap={{
                    _vertical: "100",
                  }}
                >
                  <Radio value="false">
                    {t(
                      "core:STAFF.INVITE_MODAL.LABELS.DO_NOT_SEND_INVITATION_EMAIL",
                    )}
                  </Radio>
                  <Radio value="true">
                    {t("core:STAFF.INVITE_MODAL.LABELS.SEND_INVITATION_EMAIL")}
                  </Radio>
                </RadioGroup>
              </Box>
            </Flex>
          </form>
        </FormProvider>
        {formError && (
          <Text visual={"smallSemiBold"} color={"text.error.dark"} mt={"100"}>
            {formError}
          </Text>
        )}
      </Box>
    </WhereaboutsModal>
  );
};
